import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '@/features/auth/hooks/use-user';
import { ChartConfigWithPopulatedData } from '@/features/charts/api/types';
import {
  AggregationType,
  chartToIcon,
  ChartType,
} from '@/features/charts/components/create-chart-type-selection';
import { chartConfigsQueryKey } from '@/features/charts/hooks/use-chart-configs-for-project';
import { useCreateChartMutation } from '@/features/charts/hooks/use-create-chart-mutation';
import { useUpdateChartMutation } from '@/features/charts/hooks/use-update-chart-mutation';
import { getAvailableChartTypes } from '@/features/charts/util/chart-util';
import { useCurrentOrg } from '@/features/org/hooks/use-org';
import { useProjectKpisWithMetrics } from '@/features/projects/project-details/hooks/use-project-kpis';
import { isDerivativeKpi } from '@/features/projects/shared/utils/kpi-utils';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { KpiType, ProjectType } from '@/shared/types/api';

interface ChartOption {
  id: string;
  chartType: ChartType;
  aggregationType: AggregationType;
  labelKey: string;
}

const CHART_OPTIONS: ChartOption[] = [
  {
    id: 'line_timeline',
    chartType: ChartType.LINE,
    aggregationType: AggregationType.TIMELINE,
    labelKey: 'chart_options.line',
  },
  {
    id: 'bar_timeline',
    chartType: ChartType.BAR,
    aggregationType: AggregationType.TIMELINE,
    labelKey: 'chart_options.bar_timeline',
  },
  {
    id: 'pie_accumulated',
    chartType: ChartType.PIE,
    aggregationType: AggregationType.ACCUMULATED,
    labelKey: 'chart_options.pie',
  },
  {
    id: 'bar_accumulated',
    chartType: ChartType.BAR,
    aggregationType: AggregationType.ACCUMULATED,
    labelKey: 'chart_options.bar_accumulated',
  },
  {
    id: 'battery_accumulated',
    chartType: ChartType.BATTERY,
    aggregationType: AggregationType.ACCUMULATED,
    labelKey: 'chart_options.battery',
  },
];

const findChartOptionId = (
  chartType: ChartType,
  aggregationType: AggregationType,
): string | undefined =>
  CHART_OPTIONS.find(
    (opt) =>
      opt.chartType === chartType && opt.aggregationType === aggregationType,
  )?.id;

interface BaseChartDialogProps {
  projectId: number;
  projectType: ProjectType;
}

interface CreateChartDialogProps extends BaseChartDialogProps {
  mode: 'create';
  trigger: React.ReactNode;
}

interface EditChartDialogProps extends BaseChartDialogProps {
  mode: 'edit';
  chartConfig: ChartConfigWithPopulatedData;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ChartDialogProps = CreateChartDialogProps | EditChartDialogProps;

export const ChartDialog = (props: ChartDialogProps) => {
  const { projectId, projectType, mode } = props;

  const { t } = useTranslation('chart');
  const { t: tProject } = useTranslation('project', {
    keyPrefix: 'ChartsSection',
  });
  const { data: user } = useUser();
  const { data: org } = useCurrentOrg();
  const queryClient = useQueryClient();
  const createMutation = useCreateChartMutation();
  const updateMutation = useUpdateChartMutation();

  const isEdit = mode === 'edit';
  const chartConfig = isEdit ? props.chartConfig : undefined;

  const initialKpiId = chartConfig?.kpis[0]?.id.toString();
  const initialOptionId = chartConfig
    ? findChartOptionId(
        chartConfig.chart_type as ChartType,
        chartConfig.aggregation_type as AggregationType,
      )
    : undefined;

  const [internalOpen, setInternalOpen] = useState(false);
  const [selectedKpiId, setSelectedKpiId] = useState<string | undefined>(
    initialKpiId,
  );
  const [selectedOptionId, setSelectedOptionId] = useState<string | undefined>(
    initialOptionId,
  );

  const open = isEdit ? props.open : internalOpen;
  const setOpen = isEdit ? props.onOpenChange : setInternalOpen;

  useEffect(() => {
    if (open && isEdit) {
      setSelectedKpiId(initialKpiId);
      setSelectedOptionId(initialOptionId);
    }
  }, [open, isEdit, initialKpiId, initialOptionId]);

  const [projectKpisQuery, derivativeKpisQuery] = useProjectKpisWithMetrics({
    projectId,
    projectType,
  });

  const kpis = useMemo(
    () => [
      ...(projectKpisQuery.data?.filter(Boolean) || []),
      ...(derivativeKpisQuery.data?.filter(Boolean) || []),
    ],
    [projectKpisQuery.data, derivativeKpisQuery.data],
  );

  const selectedKpi = useMemo(
    () => kpis.find((kpi) => kpi.id.toString() === selectedKpiId),
    [kpis, selectedKpiId],
  );

  const selectedOption = useMemo(
    () => CHART_OPTIONS.find((opt) => opt.id === selectedOptionId),
    [selectedOptionId],
  );

  const availableChartOptions = useMemo(() => {
    if (!selectedKpi) return [];

    const timelineTypes = getAvailableChartTypes({
      aggregationType: AggregationType.TIMELINE,
      kpiType: selectedKpi.type as KpiType,
      maxValue: selectedKpi.max_value,
    });

    const accumulatedTypes = getAvailableChartTypes({
      aggregationType: AggregationType.ACCUMULATED,
      kpiType: selectedKpi.type as KpiType,
      maxValue: selectedKpi.max_value,
    });

    return CHART_OPTIONS.filter((option) => {
      if (option.aggregationType === AggregationType.TIMELINE) {
        return timelineTypes.includes(option.chartType);
      }
      return accumulatedTypes.includes(option.chartType);
    });
  }, [selectedKpi]);

  const resetForm = () => {
    setSelectedKpiId(isEdit ? initialKpiId : undefined);
    setSelectedOptionId(isEdit ? initialOptionId : undefined);
  };

  const handleKpiChange = (value: string) => {
    setSelectedKpiId(value);
    setSelectedOptionId(undefined);
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) resetForm();
  };

  const handleSubmit = () => {
    if (!selectedKpi || !selectedOption) return;

    if (isEdit && chartConfig) {
      updateMutation.mutate(
        {
          chartId: chartConfig.id,
          chartData: {
            chartType: selectedOption.chartType,
            aggregationType: selectedOption.aggregationType,
            kpiIds: [selectedKpi.id],
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: chartConfigsQueryKey(projectId),
            });
            setOpen(false);
          },
        },
      );
    } else {
      if (!user?.id || !org?.id) return;
      createMutation.mutate(
        {
          chartData: {
            kpis: [selectedKpi],
            aggregationType: selectedOption.aggregationType,
            chartType: selectedOption.chartType,
          },
          userId: user.id,
          orgaId: org.id,
          projectId,
          isDerivative: isDerivativeKpi(selectedKpi),
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: chartConfigsQueryKey(projectId),
            });
            setOpen(false);
            resetForm();
          },
        },
      );
    }
  };

  const isPending = isEdit
    ? updateMutation.isPending
    : createMutation.isPending;
  const hasChanges =
    !isEdit ||
    selectedKpiId !== initialKpiId ||
    selectedOptionId !== initialOptionId;
  const canSubmit =
    selectedKpiId && selectedOptionId && hasChanges && !isPending;

  const dialogContent = (
    <DialogContent className="max-w-[688px] gap-8" closeInline>
      <DialogHeader className="mb-8">
        <DialogTitle className="text-3xl font-semibold text-white">
          {isEdit ? tProject('edit_chart_title') : tProject('add_chart_title')}
        </DialogTitle>
      </DialogHeader>

      <div className="flex flex-col gap-8">
        <Select value={selectedKpiId} onValueChange={handleKpiChange}>
          <SelectTrigger isInputTrigger>
            <SelectValue
              asLabelInput
              label={t('create_dialog.kpi_label')}
              placeholder={t('create_dialog.kpi_placeholder')}
            >
              {selectedKpi?.name}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {kpis.map((kpi) => (
              <SelectItem key={kpi.id} value={kpi.id.toString()}>
                {kpi.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={selectedOptionId}
          onValueChange={setSelectedOptionId}
          disabled={!selectedKpiId}
        >
          <SelectTrigger isInputTrigger>
            <SelectValue
              asLabelInput
              label={t('create_dialog.chart_type_label')}
              placeholder={t('create_dialog.chart_type_placeholder')}
              aria-disabled={!selectedKpiId}
            >
              {selectedOption ? t(selectedOption.labelKey as any) : undefined}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {availableChartOptions.map((option) => (
              <SelectItem key={option.id} value={option.id}>
                <span className="flex items-center gap-2">
                  {chartToIcon(option.chartType)}
                  {t(option.labelKey as any)}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex justify-center pt-4">
        <Button
          variant="greenGradient"
          btnType="primary"
          className="w-40"
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          {isPending
            ? isEdit
              ? t('edit_dialog.saving')
              : t('create_dialog.creating')
            : isEdit
              ? t('edit_dialog.save')
              : t('create_dialog.submit')}
        </Button>
      </div>
    </DialogContent>
  );

  if (isEdit) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        {dialogContent}
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{props.trigger}</DialogTrigger>
      {dialogContent}
    </Dialog>
  );
};

export const CreateChartDialog = (
  props: Omit<CreateChartDialogProps, 'mode'>,
) => <ChartDialog {...props} mode="create" />;

export const EditChartDialog = (props: Omit<EditChartDialogProps, 'mode'>) => (
  <ChartDialog {...props} mode="edit" />
);
